import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Location } from '@datapad/data-model';
import { Add, Check, Clear, Close, Edit } from '@mui/icons-material';
import { Box, IconButton, Link, TextField as MuiTextField, Typography } from '@mui/material';
import React from 'react';
export function InlineLocationField(props) {
    const { location, onChange, canEdit = false } = props;
    const [isEditing, setIsEditing] = React.useState(false);
    const [editName, setEditName] = React.useState(location?.name ?? '');
    const [editUrl, setEditUrl] = React.useState(location?.resourceUrl ?? '');
    const nameInputRef = React.useRef(null);
    React.useEffect(() => {
        if (!isEditing) {
            setEditName(location?.name ?? '');
            setEditUrl(location?.resourceUrl ?? '');
        }
    }, [location, isEditing]);
    React.useEffect(() => {
        if (isEditing && nameInputRef.current) {
            nameInputRef.current.focus();
        }
    }, [isEditing]);
    const handleStartEdit = React.useCallback(() => {
        if (canEdit) {
            setEditName(location?.name ?? '');
            setEditUrl(location?.resourceUrl ?? '');
            setIsEditing(true);
        }
    }, [canEdit, location]);
    const handleSave = React.useCallback(() => {
        if (editName.trim()) {
            const newLocation = new Location({
                name: editName.trim(),
                resourceUrl: editUrl.trim() || undefined,
            });
            onChange(newLocation);
        }
        else {
            onChange(undefined);
        }
        setIsEditing(false);
    }, [editName, editUrl, onChange]);
    const handleClear = React.useCallback(() => {
        onChange(undefined);
        setIsEditing(false);
    }, [onChange]);
    const handleCancel = React.useCallback(() => {
        setEditName(location?.name ?? '');
        setEditUrl(location?.resourceUrl ?? '');
        setIsEditing(false);
    }, [location]);
    const handleKeyDown = React.useCallback((event) => {
        if (event.key === 'Escape') {
            handleCancel();
        }
        else if (event.key === 'Enter') {
            event.preventDefault();
            handleSave();
        }
    }, [handleCancel, handleSave]);
    if (isEditing) {
        return (_jsxs(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 1 }, children: [_jsx(Box, { sx: { display: 'flex', alignItems: 'center', gap: 0.5 }, children: _jsx(MuiTextField, { inputRef: nameInputRef, value: editName, onChange: (e) => setEditName(e.target.value), onKeyDown: handleKeyDown, placeholder: "Location name", size: "small", fullWidth: true, variant: "outlined", label: "Name" }) }), _jsx(Box, { sx: { display: 'flex', alignItems: 'center', gap: 0.5 }, children: _jsx(MuiTextField, { value: editUrl, onChange: (e) => setEditUrl(e.target.value), onKeyDown: handleKeyDown, placeholder: "https://...", size: "small", fullWidth: true, variant: "outlined", label: "URL (optional)" }) }), _jsxs(Box, { sx: { display: 'flex', gap: 0.5 }, children: [_jsx(IconButton, { size: "small", onClick: handleSave, color: "primary", "aria-label": "save", children: _jsx(Check, { fontSize: "small" }) }), _jsx(IconButton, { size: "small", onClick: handleCancel, "aria-label": "cancel", children: _jsx(Close, { fontSize: "small" }) }), location && (_jsx(IconButton, { size: "small", onClick: handleClear, color: "error", "aria-label": "clear location", children: _jsx(Clear, { fontSize: "small" }) }))] })] }));
    }
    if (!location) {
        if (!canEdit) {
            return (_jsx(Typography, { variant: "body2", color: "text.disabled", sx: { fontStyle: 'italic' }, children: "No location" }));
        }
        return (_jsxs(Box, { sx: {
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                cursor: 'pointer',
                '&:hover': {
                    backgroundColor: 'action.hover',
                    borderRadius: 1,
                },
                padding: 0.5,
                marginLeft: -0.5,
                borderRadius: 1,
            }, onClick: handleStartEdit, children: [_jsx(Typography, { variant: "body2", color: "text.disabled", sx: { fontStyle: 'italic' }, children: "Add location" }), _jsx(Add, { fontSize: "small", sx: {
                        color: 'action.active',
                        opacity: 0.5,
                        '&:hover': { opacity: 1 },
                    } })] }));
    }
    return (_jsxs(Box, { sx: {
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            cursor: canEdit ? 'pointer' : 'default',
            '&:hover': canEdit
                ? {
                    backgroundColor: 'action.hover',
                    borderRadius: 1,
                }
                : {},
            padding: 0.5,
            marginLeft: -0.5,
            borderRadius: 1,
        }, onClick: canEdit ? handleStartEdit : undefined, children: [_jsxs(Typography, { variant: "body2", component: "span", children: [_jsx("strong", { children: "Location:" }), ' ', location.resourceUrl ? (_jsx(Link, { href: location.resourceUrl, target: "_blank", rel: "noopener noreferrer", onClick: (e) => e.stopPropagation(), children: location.name })) : (location.name)] }), canEdit && (_jsx(Edit, { fontSize: "small", sx: {
                    color: 'action.active',
                    opacity: 0.5,
                    '&:hover': { opacity: 1 },
                } }))] }));
}
//# sourceMappingURL=InlineLocationField.js.map